import React from 'react';
import { Chip } from '@mui/material';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import { Tooltip } from 'react-scale/components/design-system';
// eslint-disable-next-line restrict-imports/restrict-imports
import { withStyles } from '@material-ui/styles';

const StyledChip = withStyles({
  root: {
    width: '70%',
    position: 'relative',
  },
  deleteIcon: {
    position: 'absolute',
    right: 0,
  },
})(Chip);

type ChipColor =
  | 'default'
  | 'info'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'success'
  | 'warning'
  | undefined;

export interface IChipListProps<T> {
  items: T[];
  getLabel?: (item: T) => string;
  getColor?: (item: T) => ChipColor;
  getTooltip?: (item: T) => React.ReactNode;
  maxItems?: number;
  inline?: boolean;
  equalWidth?: boolean;
  itemType?: string;
  readOnly?: boolean;
  noBorder?: boolean;
  color?: ChipColor;
  readOnlyIf?: (item: T) => boolean;
  onDelete?: (item: T) => void;
}

export function ChipList<T>(props: IChipListProps<T>) {
  const { items, inline, itemType, maxItems, readOnly, color, equalWidth, noBorder } = props;
  const { getLabel, getColor, getTooltip, readOnlyIf, onDelete } = props;
  const itemLimit = maxItems ?? items?.length;

  const isReadOnly = React.useCallback(
    (item: T) => {
      return readOnly || !onDelete || (!!readOnlyIf && readOnlyIf(item));
    },
    [readOnly, readOnlyIf, onDelete],
  );

  const onDeleteClick = React.useCallback(
    (item: T) => {
      if (isReadOnly(item) || !onDelete) return;
      return onDelete(item);
    },
    [onDelete, isReadOnly],
  );

  const getChipColor = React.useCallback(
    (item: T) => {
      return (getColor && getColor(item)) || color || 'info';
    },
    [getColor, color],
  );

  const itemTypeLabel = React.useMemo(() => itemType || 'item', [itemType]);
  const getItemLabel = React.useCallback(
    (item: T) => (getLabel ? getLabel(item) : String(item)),
    [getLabel],
  );
  const chipItems = React.useMemo(() => {
    return items
      .slice(0, itemLimit)
      .map(item => ({ label: getItemLabel(item), value: item, sourceItem: item }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [items, getItemLabel, itemLimit]);

  const itemWidth = React.useMemo(() => {
    if (!equalWidth) return {};
    const maxLength = _.maxBy(chipItems, item => item.label.length)?.label.length ?? 0;
    return { width: `${maxLength + 5}ch` };
  }, [equalWidth, chipItems]);

  return (
    <div
      style={{
        border: noBorder ? '' : '1px dashed lightgray',
        padding: '1ex',
        borderRadius: '1ex',
        display: inline ? 'inline' : '',
      }}
    >
      {chipItems.map(item => {
        const itemLabel = getItemLabel(item.value);
        const itemChip = (
          <StyledChip
            key={itemLabel}
            color={getChipColor(item.value)}
            label={itemLabel}
            size="small"
            style={{ margin: 1, fontFamily: 'monospace', ...itemWidth }}
            onDelete={isReadOnly(item.value) ? undefined : () => onDeleteClick(item.value)}
          />
        );
        if (!getTooltip) return itemChip;
        return (
          <Tooltip key={`${itemLabel}-tooltip`} title={getTooltip(item.sourceItem)}>
            {itemChip}
          </Tooltip>
        );
      })}
      {items.length > itemLimit && (
        <Typography variant="body2">
          ... and {items.length - itemLimit} more {itemType || 'item'}s.
        </Typography>
      )}
      {items.length === 0 && <Typography variant="body2">No {itemTypeLabel}s selected.</Typography>}
    </div>
  );
}
